// Local translations for component dialog
export const T = {
  en_GB: {
    // Dialog
    addPdfComponent: "Add PDF Component",
    selectComponentType: "Select the type of component you want to add",
    settings: "Settings",
    configureComponent: "Configure",
    configureSettings: "Configure the component settings",
    selectComponentPlaceholder: "Please select a component type",
    searchComponents: "Search components...",
    noComponentsFound: "No components found",
    next: "Next",
    back: "Back",
    cancel: "Cancel",
    save: "Save",
    addComponent: "Add Component",
    componentSettings: "Component Settings",
    conditionalDisplay: "Conditional Display",
    enableConditionalDisplay: "Enable Conditional Display",
    showComponentWhen: "Show this component only when a condition is met",
    when: "When",
    selectField: "Select a field...",
    searchFields: "Search fields…",
    noFieldsAvailable: "No fields available.",
    noFieldsMatch: "No fields match your search.",
    condition: "Condition",
    isEmpty: "Is Empty",
    isNotEmpty: "Is Not Empty",
    contains: "Contains",
    notContains: "Does Not Contain",
    value: "Value",
    selectValue: "Select a value...",
    searchCountries: "Search countries…",
    noCountriesMatch: "No countries match your search.",
    enterValue: "Enter value...",
    missingQuizPickOneOf: (names: string) =>
      `The quiz this rule points at no longer exists. Pick one of: ${names}`,
    missingQuizNoneLeft:
      "The quiz this rule points at no longer exists, and this flow has no quiz components left.",
    noSettingsRequired: "This component has no additional settings.",
    requirements: "Requirements:",
    requiresPerGoal: "Must be placed in a per-goal section",
    riskPrintsHere: "Prints here:",
    riskPrintsNothing:
      "This flow's risk model captures every risk input on the other section scope, so this placement would print nothing",
    riskNeedsAdjustments:
      "Only the knowledge and experience questions print here, so switch on the score-derivation rows in the component settings or the block prints nothing",
    riskSectionTimeHorizon: "time horizon",
    riskSectionRiskLevel: "risk tolerance",
    riskSectionAdjustmentQuestions: "knowledge and experience questions",
    riskSectionResult: "result (score and derivation)",
    requiresRiskModel: "Requires risk model to be configured on the flow",
    requiresPortfolioCollection: "Requires portfolio collection to be configured on the flow",
    requiresXRay: "Requires Morningstar X-Ray access",
    requiresAssetAllocationSetting:
      'Enable the collection\'s "Requires asset allocation" setting to use this component',
    requiresTimeseriesSetting:
      'Enable the collection\'s "Requires timeseries" setting to use this component',
    selectComponentDetails: "Select a component type to see details",
    // Category labels
    categoryGeneral: "General",
    categoryData: "Data",
    categoryPortfolio: "Portfolio",
    categoryCosts: "Costs",
    categoryClient: "Client",
    categoryGoal: "Goal",
    // Component descriptions (short)
    textDescription: "Add formatted text content",
    headingDescription: "Add a section heading",
    tableOfContentsDescription: "List the document's headings with page numbers",
    imageDescription: "Insert an image",
    advisorImageDescription: "Display advisor profile image",
    pageBreakDescription: "Force a new page",
    spacerDescription: "Add vertical spacing",
    tableDescription: "Display data in a table",
    portfolioDisplayDescription: "Show portfolio holdings",
    portfolioXrayDescription: "Detailed portfolio analysis",
    assetAllocationDescription: "Asset allocation breakdown",
    assetClassForecastDescription: "Asset class mix over time",
    portfolioForecastDescription: "Portfolio value projections",
    expectedValueDescription: "Expected value over time",
    riskAssessmentDescription: "Risk assessment results",
    historicalReturnDescription: "Indexed historical return chart",
    costTableDescription: "Summary of all costs",
    costChartDescription: "Cost projection visualization",
    portfolioCostsDescription: "Advisory and platform fees",
    productCostsDescription: "Product-level cost breakdown",
    clientClassificationDescription: "MiFID II classification",
    productAssessmentDescription: "Investment experience overview",
    quizResultsDescription: "Knowledge quiz results",
    trapetsLookupDescription: "PEP and sanctions screening",
    sumsubVerificationDescription: "Sumsub identity verification results",
    eninLookupDescription: "Company risk flags from the ENIN registry",
    groupMembersDescription: "Table of the group's members",
    clientPartiesDescription: "Table of representatives, POA and guardians",
    goalInfoDescription: "Investment goal details",
    goalListDescription: "Table of all goals in the flow instance",
    wealthPlanDescription: "Withdrawal and income schedule",
    sustainabilityPreferencesDescription: "The client's stated sustainability preferences",
    sustainabilityStatusDescription: "Per-goal sustainability matching table",
    clientPreferencesDescription: "The client's preference answers",
    financialSituationDescription: "The client's economy as captured in the flow",
    financialSituationDetailedDescription:
      "Prints the client's financial situation as captured in the flow — income, assets, liabilities, commitments, capacity and the summary measures. Choose which parts to print in the component's settings. Amounts are shown in the flow's currency.",
    requiresFinancialSituation: "The flow needs a Financial situation component",
    volatilityChartDescription: "Goal-level volatility over time with compliance gate",
    equityShareChartDescription: "Goal-level equity share over time with compliance gate",
    mandateDescription:
      "The mandate the advice is given under and the goal's allocation against it",
    mandateComparisonDescription:
      "Follow-up: the bank's existing mandate beside the one this advice proposes",
    mandatePortfolioDescription: "This portfolio's own allocation against the mandate",
    // Component descriptions (detailed)
    textDetailedDescription:
      "Add formatted text content with support for dynamic fields like client name, dates, and flow data. Supports rich text formatting.",
    headingDetailedDescription:
      "Add a section heading with customizable style. Great for organizing your PDF into clear sections.",
    tableOfContentsDetailedDescription:
      "Lists the Heading components in the document, in document order, each with the page it is on. Titles belonging to other components — tables, charts and X-rays — are not listed. Usually placed on its own page after the cover.",
    tableOfContentsListed:
      "What is listed: every Heading component, in document order. Titles on other components — tables, charts and X-rays — are not listed. A heading hidden by a conditional display rule is left out, and a heading in a per-goal or per-portfolio section is listed once per goal or portfolio, with its fields resolved.",
    requiresHeadings: "The document needs at least one Heading component",
    imageDetailedDescription:
      "Insert a static image from your media library. Useful for logos, charts, and decorative elements.",
    advisorImageDetailedDescription:
      "Display the advisor's profile image. The image is automatically pulled from the user's profile.",
    pageBreakDetailedDescription:
      "Force the content to continue on a new page. Useful for separating major sections.",
    spacerDetailedDescription:
      "Add vertical spacing between components. Customize the height to control layout.",
    tableDetailedDescription:
      "Display data in a structured table format. Configure columns, headers, and data sources.",
    portfolioDisplayDetailedDescription:
      "Show the portfolio holdings with product names, allocations, and values. Customizable display options.",
    portfolioXrayDetailedDescription:
      "Display detailed Morningstar X-Ray analysis including asset allocation, sector exposure, and style box.",
    assetAllocationDetailedDescription:
      "Show how the portfolio is distributed across different asset classes with chart and table options.",
    assetClassForecastDetailedDescription:
      "Show how the asset class mix shifts over time as each portfolio grows at its own rate, over the time range you choose.",
    portfolioForecastDetailedDescription:
      "Display projected portfolio value over time based on expected returns and contributions.",
    expectedValueDetailedDescription:
      "Show the expected value trajectory of the investment over the planning horizon.",
    riskAssessmentDetailedDescription:
      "Display the client's risk assessment results including risk score and profile.",
    costTableDetailedDescription:
      "Show a comprehensive summary of all investment-related costs including product costs, advisory fees, and platform fees.",
    historicalReturnDetailedDescription:
      "Display the goal portfolio's indexed historical return over time as a line chart.",
    costChartDetailedDescription:
      "Visualize the impact of costs on portfolio value over time with a projection chart.",
    portfolioCostsDetailedDescription: "Display detailed breakdown of advisory and platform fees.",
    productCostsDetailedDescription:
      "Show product-level costs including ongoing charges, transaction costs, and incidental costs.",
    clientClassificationDetailedDescription:
      "Display the client's MiFID II classification (Retail, Professional, or Eligible Counterparty).",
    productAssessmentDetailedDescription:
      "Show an overview of the client's investment experience and product knowledge.",
    quizResultsDetailedDescription:
      "Show the client's knowledge quiz results, including score, pass status and who answered.",
    trapetsLookupDetailedDescription:
      "Display results from PEP (Politically Exposed Person) and sanctions screening.",
    sumsubVerificationDetailedDescription:
      "Display Sumsub identity verification results including verification status, name, date of birth, country, and document expiry.",
    eninLookupDetailedDescription:
      "Display the ENIN company registry lookup result including the negative-flags verdict and any active risk flags.",
    groupMembersDetailedDescription:
      "Display a table of the group's members with SSN, contact details and representative role. Renders only for Group clients with registered members.",
    clientPartiesDetailedDescription:
      "Display a table of the client's related parties (representatives, powers of attorney, guardians) with authority basis, validity and verification status. Renders only when parties are registered.",
    goalInfoDetailedDescription:
      "Show key details about the investment goal including name, target amount, and timeline.",
    goalListDetailedDescription:
      "Display a table of every goal in the flow instance with its name, goal type, platform and description. Can be placed in any section.",
    wealthPlanDetailedDescription:
      "Display the planned withdrawals and income schedule from the portfolio.",
    sustainabilityPreferencesDetailedDescription:
      "Table of the client's sustainability preferences: criterion granulations, chosen product preferences, and the portfolio alignment mode (or 'Not important' when declined).",
    sustainabilityStatusDetailedDescription:
      "Per-product sustainability matching for each of the goal's portfolios with the goal-level status and the advisor's deviation decision.",
    clientPreferencesDetailedDescription:
      "Table of the client's preference answers (Yes/No per preference). For completed advice sessions the answers are frozen at submission and never change afterwards.",
    volatilityChartDetailedDescription:
      "Displays the goal's blended portfolio volatility over the chosen time range with the risk model's ceiling, the baseline (for follow-ups), and the advisor's deviation decision with justification.",
    equityShareChartDetailedDescription:
      "Displays the goal's blended equity share over the chosen time range as a stacked bar (equity, non-equity, unknown) with the risk model's ceiling, the baseline (for follow-ups), and the advisor's deviation decision with justification.",
    mandateDetailedDescription:
      "Prints the mandate the advice is given under, one row per instrument with the goal's combined weight at the end of year one against what the mandate permits, holdings outside the mandate, and the accepted deviation with the advisor's note, who accepted it and when. Under portfolio-level blocking each portfolio's own verdict follows. Frozen when the advice is submitted. Prints one line when the goal has no mandate — use the 'Goal: Has Mandate' rule to hide it.",
    mandateComparisonDetailedDescription:
      "On a follow-up that proposes a new mandate, prints the bank's existing mandate beside the proposed one, one row per instrument, each side's minimum – default – maximum (or fixed weight), the new side bold where it changed. Prints nothing while the goal has one side only.",
    mandatePortfolioDetailedDescription:
      "Prints one portfolio's own mandate check: the value it was checked on (its projected value at the end of year one, or the new money when the collection checks purchases), one row per instrument with this portfolio's weight against what the mandate permits, holdings outside the mandate, and its verdict. Under portfolio-level blocking the portfolio's own decision follows — the accepted deviation with the advisor's note, who accepted it and when, or 'Do not proceed'; under goal-level blocking the verdict is printed for information and the decision stays in the goal's Mandate block. Frozen when the advice is submitted. Prints one line when the goal has no mandate or the portfolio was not checked — note that the 'Portfolio: Has Mandate Check' rule is Yes only under portfolio-level blocking.",
    // Conditional display fields
    clientCountry: "Client Country",
    clientType: "Client Type",
    classificationType: "Classification Type",
    trapetsPep: "Trapets: PEP Status",
    expectingFinancialChanges: "Expecting Financial Changes",
    trapetsSanctioned: "Trapets: Sanctioned Status",
    sumsubVerified: "Sumsub: Verified Status",
    sumsubDocumentCountry: "Sumsub: Document Country",
    followupMode: "Follow-up Mode",
    goalRiskScore: "Goal: Risk Score",
    goalType: "Goal: Type",
    isOneOf: "Is One Of",
    isNotOneOf: "Is Not One Of",
    portfolioType: "Portfolio: Portfolio Type",
    portfolioScheduleType: "Portfolio: Schedule Type",
    portfolioHasMandateCheck: "Portfolio: Has Mandate Check",
    portfolioMandateStatus: "Portfolio: Mandate Status",
    portfolioModelDeviation: "Portfolio: Model From Another Risk Score",
    modelDeviationYes: "Yes",
    modelDeviationNo: "No",
    modelDeviationNotModel: "Not a model portfolio",
    mandateStatusOk: "Within range",
    mandateStatusAccepted: "Deviation accepted",
    mandateStatusDeviation: "Breach",
    mandateStatusNotAccepted: "Blocked",
    mandateStatusUnchecked: "Not checked",
    goalHasRisk: "Goal: Has Risk Assessment",
    goalHasMandate: "Goal: Has Mandate",
    goalRiskScoreCapped: "Goal: Risk Score Capped",
    capStateYes: "Yes",
    capStateNo: "No",
    capStateNoRiskAssessment: "No risk assessment",
    capStateNoClientFieldData: "Cap could not be assessed",
    flowStatus: "Flow Status",
    clientField: "Client Field:",
    quizField: "Quiz:",
    flowField: "Flow Field:",
  },
  nb_NO: {
    // Dialog
    addPdfComponent: "Legg til PDF-komponent",
    selectComponentType: "Velg typen komponent du vil legge til",
    settings: "Innstillinger",
    configureComponent: "Konfigurer",
    configureSettings: "Konfigurer komponentinnstillingene",
    selectComponentPlaceholder: "Vennligst velg en komponenttype",
    searchComponents: "Søk komponenter...",
    noComponentsFound: "Ingen komponenter funnet",
    next: "Neste",
    back: "Tilbake",
    cancel: "Avbryt",
    save: "Lagre",
    addComponent: "Legg til komponent",
    componentSettings: "Komponentinnstillinger",
    conditionalDisplay: "Betinget visning",
    enableConditionalDisplay: "Aktiver betinget visning",
    showComponentWhen: "Vis denne komponenten bare når en betingelse er oppfylt",
    when: "Når",
    selectField: "Velg et felt...",
    searchFields: "Søk i felt…",
    noFieldsAvailable: "Ingen felt tilgjengelig.",
    noFieldsMatch: "Ingen felt samsvarer med søket.",
    condition: "Betingelse",
    isEmpty: "Er tom",
    isNotEmpty: "Er ikke tom",
    contains: "Inneholder",
    notContains: "Inneholder ikke",
    value: "Verdi",
    selectValue: "Velg en verdi...",
    searchCountries: "Søk i land…",
    noCountriesMatch: "Ingen land samsvarer med søket.",
    enterValue: "Skriv inn verdi...",
    missingQuizPickOneOf: (names: string) =>
      `Quizen denne regelen peker på finnes ikke lenger. Velg en av: ${names}`,
    missingQuizNoneLeft:
      "Quizen denne regelen peker på finnes ikke lenger, og denne flyten har ingen quizkomponenter igjen.",
    noSettingsRequired: "Denne komponenten har ingen ekstra innstillinger.",
    requirements: "Krav:",
    requiresPerGoal: "Må plasseres i en per-mål-seksjon",
    riskPrintsHere: "Skrives ut her:",
    riskPrintsNothing:
      "Risikomodellen til denne flyten fanger alle risikoinnspill på den andre seksjonstypen, så denne plasseringen ville ikke skrevet ut noe",
    riskNeedsAdjustments:
      "Bare kunnskaps- og erfaringsspørsmålene skrives ut her, så slå på radene for skårutledning i komponentinnstillingene, ellers skriver blokken ikke ut noe",
    riskSectionTimeHorizon: "tidshorisont",
    riskSectionRiskLevel: "risikotoleranse",
    riskSectionAdjustmentQuestions: "kunnskaps- og erfaringsspørsmål",
    riskSectionResult: "resultat (skår og utledning)",
    requiresRiskModel: "Krever at risikomodell er konfigurert på flyten",
    requiresPortfolioCollection: "Krever at porteføljesamling er konfigurert på flyten",
    requiresXRay: "Krever Morningstar X-Ray-tilgang",
    requiresAssetAllocationSetting:
      'Aktiver samlingens "Krever aktivaallokering"-innstilling for å bruke denne komponenten',
    requiresTimeseriesSetting:
      'Aktiver samlingens "Krever tidsserie"-innstilling for å bruke denne komponenten',
    selectComponentDetails: "Velg en komponenttype for å se detaljer",
    // Category labels
    categoryGeneral: "Generelt",
    categoryData: "Data",
    categoryPortfolio: "Portefølje",
    categoryCosts: "Kostnader",
    categoryClient: "Kunde",
    categoryGoal: "Mål",
    // Component descriptions (short)
    textDescription: "Legg til formatert tekstinnhold",
    headingDescription: "Legg til en seksjonsoverskrift",
    tableOfContentsDescription: "Vis dokumentets overskrifter med sidetall",
    imageDescription: "Sett inn et bilde",
    advisorImageDescription: "Vis rådgivers profilbilde",
    pageBreakDescription: "Tving en ny side",
    spacerDescription: "Legg til vertikal avstand",
    tableDescription: "Vis data i en tabell",
    portfolioDisplayDescription: "Vis porteføljebeholdning",
    portfolioXrayDescription: "Detaljert porteføljeanalyse",
    assetAllocationDescription: "Aktivaallokeringsoversikt",
    assetClassForecastDescription: "Aktivaklassefordeling over tid",
    portfolioForecastDescription: "Porteføljeverdi-prognoser",
    expectedValueDescription: "Forventet verdi over tid",
    riskAssessmentDescription: "Risikovurderingsresultater",
    costTableDescription: "Sammendrag av alle kostnader",
    historicalReturnDescription: "Indeksert historisk avkastningsdiagram",
    costChartDescription: "Kostnadsprojeksjonsvisualisering",
    portfolioCostsDescription: "Rådgivnings- og plattformgebyrer",
    productCostsDescription: "Produktnivå kostnadsoversikt",
    clientClassificationDescription: "MiFID II-klassifisering",
    productAssessmentDescription: "Investeringserfaringsoversikt",
    quizResultsDescription: "Kunnskapsquiz-resultater",
    trapetsLookupDescription: "PEP- og sanksjonsscreening",
    sumsubVerificationDescription: "Sumsub identitetsverifiseringsresultater",
    eninLookupDescription: "Selskapsrisikoflagg fra ENIN-registeret",
    groupMembersDescription: "Tabell over gruppens medlemmer",
    clientPartiesDescription: "Tabell over representanter, fullmektiger og verger",
    goalInfoDescription: "Investeringsmåldetaljer",
    goalListDescription: "Tabell over alle mål i flyten",
    wealthPlanDescription: "Uttaks- og inntektsplan",
    sustainabilityPreferencesDescription: "Kundens angitte bærekraftspreferanser",
    sustainabilityStatusDescription: "Bærekraftsmatching per mål",
    clientPreferencesDescription: "Kundens preferansesvar",
    financialSituationDescription: "Kundens økonomi slik den er registrert i flyten",
    financialSituationDetailedDescription:
      "Skriver ut kundens økonomiske situasjon slik den er registrert i flyten — inntekt, eiendeler, gjeld, forpliktelser, kapasitet og nøkkeltallene. Velg hvilke deler som skal skrives ut i komponentens innstillinger. Beløp vises i flytens valuta.",
    requiresFinancialSituation: "Flyten trenger en Økonomisk situasjon-komponent",
    volatilityChartDescription: "Volatilitet over tid med samsvarskontroll per mål",
    equityShareChartDescription: "Aksjeandel over tid med samsvarskontroll per mål",
    mandateDescription: "Mandatet rådet gis under, og målets allokering mot det",
    mandateComparisonDescription:
      "Oppfølging: bankens eksisterende mandat ved siden av det dette rådet foreslår",
    mandatePortfolioDescription: "Denne porteføljens egen allokering mot mandatet",
    // Component descriptions (detailed)
    textDetailedDescription:
      "Legg til formatert tekstinnhold med støtte for dynamiske felt som kundenavn, datoer og flytdata. Støtter rik tekstformatering.",
    headingDetailedDescription:
      "Legg til en seksjonsoverskrift med tilpassbar stil. Flott for å organisere PDF-en i tydelige seksjoner.",
    tableOfContentsDetailedDescription:
      "Viser Overskrift-komponentene i dokumentet, i dokumentets rekkefølge, hver med siden den står på. Titler som hører til andre komponenter — tabeller, diagrammer og X-ray — vises ikke. Plasseres vanligvis på en egen side etter forsiden.",
    tableOfContentsListed:
      "Hva som vises: hver Overskrift-komponent, i dokumentets rekkefølge. Titler på andre komponenter — tabeller, diagrammer og X-ray — vises ikke. En overskrift som skjules av en betingelse utelates, og en overskrift i en per-mål- eller per-portefølje-seksjon vises én gang per mål eller portefølje, med feltene fylt ut.",
    requiresHeadings: "Dokumentet trenger minst én Overskrift-komponent",
    imageDetailedDescription:
      "Sett inn et statisk bilde fra mediebiblioteket. Nyttig for logoer, diagrammer og dekorative elementer.",
    advisorImageDetailedDescription:
      "Vis rådgiverens profilbilde. Bildet hentes automatisk fra brukerens profil.",
    pageBreakDetailedDescription:
      "Tving innholdet til å fortsette på en ny side. Nyttig for å skille store seksjoner.",
    spacerDetailedDescription:
      "Legg til vertikal avstand mellom komponenter. Tilpass høyden for å kontrollere layout.",
    tableDetailedDescription:
      "Vis data i et strukturert tabellformat. Konfigurer kolonner, overskrifter og datakilder.",
    portfolioDisplayDetailedDescription:
      "Vis porteføljebeholdningen med produktnavn, allokeringer og verdier. Tilpassbare visningsalternativer.",
    portfolioXrayDetailedDescription:
      "Vis detaljert Morningstar X-Ray-analyse inkludert aktivaallokering, sektoreksponering og stilboks.",
    assetAllocationDetailedDescription:
      "Vis hvordan porteføljen er fordelt på ulike aktivaklasser med diagram- og tabellalternativer.",
    assetClassForecastDetailedDescription:
      "Vis hvordan aktivaklassefordelingen endrer seg over tid etter hvert som hver portefølje vokser i sitt eget tempo, over tidsrommet du velger.",
    portfolioForecastDetailedDescription:
      "Vis forventet porteføljeverdi over tid basert på forventet avkastning og bidrag.",
    expectedValueDetailedDescription:
      "Vis den forventede verdibanen for investeringen over planleggingshorisonten.",
    riskAssessmentDetailedDescription:
      "Vis kundens risikovurderingsresultater inkludert risikoscore og profil.",
    costTableDetailedDescription:
      "Vis et omfattende sammendrag av alle investeringsrelaterte kostnader inkludert produktkostnader, rådgivningsgebyrer og plattformgebyrer.",
    historicalReturnDetailedDescription:
      "Vis målets porteføljes indekserte historiske avkastning over tid som et linjediagram.",
    costChartDetailedDescription:
      "Visualiser påvirkningen av kostnader på porteføljeverdien over tid med et projeksjonsdiagram.",
    portfolioCostsDetailedDescription:
      "Vis detaljert oversikt over rådgivnings- og plattformgebyrer.",
    productCostsDetailedDescription:
      "Vis produktnivåkostnader inkludert løpende kostnader, transaksjonskostnader og tilfeldige kostnader.",
    clientClassificationDetailedDescription:
      "Vis kundens MiFID II-klassifisering (Privatkunde, Profesjonell eller Kvalifisert motpart).",
    productAssessmentDetailedDescription:
      "Vis en oversikt over kundens investeringserfaring og produktkunnskap.",
    quizResultsDetailedDescription:
      "Vis kundens kunnskapsquiz-resultater, inkludert poengsum, beståttstatus og hvem som svarte.",
    trapetsLookupDetailedDescription:
      "Vis resultater fra PEP (Politisk eksponert person) og sanksjonsscreening.",
    sumsubVerificationDetailedDescription:
      "Vis Sumsub identitetsverifiseringsresultater inkludert verifiseringsstatus, navn, fodselsdato, land og dokumentutlop.",
    eninLookupDetailedDescription:
      "Vis resultatet fra ENIN-selskapsoppslaget inkludert vurdering av negative flagg og eventuelle aktive risikoflagg.",
    groupMembersDetailedDescription:
      "Vis en tabell over gruppens medlemmer med fødselsnummer, kontaktinformasjon og representantrolle. Vises kun for gruppekunder med registrerte medlemmer.",
    clientPartiesDetailedDescription:
      "Vis en tabell over kundens relaterte parter (representanter, fullmektiger, verger) med fullmaktsgrunnlag, gyldighet og verifiseringsstatus. Vises kun når parter er registrert.",
    goalInfoDetailedDescription:
      "Vis nøkkeldetaljer om investeringsmålet inkludert navn, målbeløp og tidsramme.",
    goalListDetailedDescription:
      "Vis en tabell over alle målene i flyten med navn, måltype, plattform og beskrivelse. Kan plasseres i alle seksjoner.",
    wealthPlanDetailedDescription: "Vis de planlagte uttakene og inntektsplanen fra porteføljen.",
    sustainabilityPreferencesDetailedDescription:
      "Tabell over kundens bærekraftspreferanser: kriterienivåer, valgte produktpreferanser og porteføljetilpasningsmodus (eller 'Ikke viktig' ved avslag).",
    sustainabilityStatusDetailedDescription:
      "Bærekraftsmatching per produkt for hver av målets porteføljer med status på målnivå og rådgiverens avviksbeslutning.",
    clientPreferencesDetailedDescription:
      "Tabell over kundens preferansesvar (Ja/Nei per preferanse). For fullførte rådgivningsøkter fryses svarene ved innsending og endres aldri i etterkant.",
    volatilityChartDetailedDescription:
      "Viser målets samlede porteføljevolatilitet over det valgte tidsrommet med risikomodellens takgrense, grunnlinje (for oppfølginger) og rådgiverens avviksbeslutning med begrunnelse.",
    equityShareChartDetailedDescription:
      "Viser målets samlede aksjeandel over det valgte tidsrommet som stablet stolpediagram (aksjer, ikke-aksjer, ukjent) med risikomodellens takgrense, grunnlinje (for oppfølginger) og rådgiverens avviksbeslutning med begrunnelse.",
    mandateDetailedDescription:
      "Skriver ut mandatet rådet gis under, én rad per instrument med målets samlede vekt ved utgangen av år én mot det mandatet tillater, beholdninger utenfor mandatet, og det aksepterte avviket med rådgiverens begrunnelse, hvem som aksepterte det og når. Ved kontroll per portefølje følger hver porteføljes egen vurdering. Fryses når rådet sendes inn. Skriver én linje når målet ikke har mandat — bruk regelen 'Mål: Har mandat' for å skjule den.",
    mandateComparisonDetailedDescription:
      "I en oppfølging som foreslår et nytt mandat, skrives bankens eksisterende mandat ved siden av det foreslåtte, én rad per instrument, hver sides minimum – standard – maksimum (eller faste vekt), den nye siden i fet der den er endret. Skriver ingenting mens målet bare har én side.",
    mandatePortfolioDetailedDescription:
      "Skriver ut én porteføljes egen mandatkontroll: verdien den ble kontrollert på (forventet verdi ved utgangen av år én, eller de nye pengene når samlingen kontrollerer kjøpene), én rad per instrument med denne porteføljens vekt mot det mandatet tillater, beholdninger utenfor mandatet, og dens vurdering. Ved blokkering per portefølje følger porteføljens egen beslutning — det aksepterte avviket med rådgiverens notat, hvem som aksepterte det og når, eller 'Ikke fortsett'; ved blokkering på målnivå skrives vurderingen ut til informasjon, og beslutningen står i målets Mandat-blokk. Fryses når rådet sendes inn. Skriver ut én linje når målet ikke har noe mandat eller porteføljen ikke ble kontrollert — merk at regelen 'Portefølje: Har mandatkontroll' bare er Ja ved blokkering per portefølje.",
    // Conditional display fields
    clientCountry: "Kundeland",
    clientType: "Kundetype",
    classificationType: "Klassifiseringstype",
    trapetsPep: "Trapets: PEP-status",
    expectingFinancialChanges: "Forventede økonomiske endringer",
    trapetsSanctioned: "Trapets: Sanksjonsstatus",
    sumsubVerified: "Sumsub: Verifiseringsstatus",
    sumsubDocumentCountry: "Sumsub: Dokumentland",
    followupMode: "Oppfølgingsmodus",
    goalRiskScore: "Mål: Risikoscore",
    goalType: "Mål: Måltype",
    isOneOf: "Er en av",
    isNotOneOf: "Er ikke en av",
    portfolioType: "Portefølje: Porteføljetype",
    portfolioScheduleType: "Portefølje: Plantype",
    portfolioHasMandateCheck: "Portefølje: Har mandatkontroll",
    portfolioMandateStatus: "Portefølje: Mandatstatus",
    portfolioModelDeviation: "Portefølje: Modell fra en annen risikoscore",
    modelDeviationYes: "Ja",
    modelDeviationNo: "Nei",
    modelDeviationNotModel: "Ikke en modellportefølje",
    mandateStatusOk: "Innenfor",
    mandateStatusAccepted: "Avvik akseptert",
    mandateStatusDeviation: "Brudd",
    mandateStatusNotAccepted: "Blokkert",
    mandateStatusUnchecked: "Ikke kontrollert",
    goalHasRisk: "Mål: Har risikovurdering",
    goalHasMandate: "Mål: Har mandat",
    goalRiskScoreCapped: "Mål: Risikoscore begrenset",
    capStateYes: "Ja",
    capStateNo: "Nei",
    capStateNoRiskAssessment: "Ingen risikovurdering",
    capStateNoClientFieldData: "Begrensningen kunne ikke vurderes",
    flowStatus: "Flytstatus",
    clientField: "Kundefelt:",
    quizField: "Quiz:",
    flowField: "Flytfelt:",
  },
} as const;
